/**
 * LobbyService — Network-driven data source for the lobby screen.
 *
 * Subscribes to the lobby socket channel via NetworkManager, maintains the
 * presence / room / chat caches the lobby UI components consume, and exposes
 * the action API the screen invokes. Wire types from @sdl/shared are
 * translated to the display shapes the badge/cell components already use,
 * so no UI component changed when we moved off the in-memory mocks.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { NetworkManager } from '../network/NetworkManager';
import type { PlayerStatus, RingColor } from '../ui/components/profile/PlayerProfileBadge';
import type {
  LobbyPlayer as WireLobbyPlayer,
  LobbyRoom as WireLobbyRoom,
  LobbyRoomPlayer as WireLobbyRoomPlayer,
  LobbyChatMessageData as WireChatMessage,
  LobbyStatePayload,
  LobbyPlayerJoinedPayload,
  LobbyPlayerLeftPayload,
  LobbyPlayerUpdatedPayload,
  LobbyChatMessagePayload,
  LobbyRoomUpsertPayload,
  LobbyRoomRemovedPayload,
  LobbyCreateRoomPayload,
} from '@sdl/shared';

export type LobbyGameMode = 'ceelo' | 'craps';

/** Display shape consumed by PlayerShowcase / PlayersGridOverlay / RoomCell. */
export interface LobbyPlayer {
  id: string;
  username: string;
  ringColor: RingColor;
  picture?: string;
  status: PlayerStatus;
  /** Division pill (skipped while leagues are off). */
  division?: string;
  /** Pre-formatted streak / today-net line, e.g. "+240K TODAY", "🔥 W3 STREAK". */
  streak?: string;
  streakCold?: boolean;
}

export interface LobbyRoom {
  id: string;
  name: string;
  mode: LobbyGameMode;
  minStake: number;        // == wire `buyIn`; UI labels it as "MIN"
  maxPlayers: number;
  playerIds: string[];     // ordered by join time; resolve via getPlayer()
  private?: boolean;
}

export type ChatMessageKind = 'text' | 'system';

export interface ChatMessage {
  id: string;
  kind: ChatMessageKind;
  playerId?: string;
  username?: string;
  picture?: string;
  ringColor?: RingColor;
  text: string;
  timestamp: number;
}

interface LobbyServiceEvents {
  'players:updated': { players: LobbyPlayer[] };
  'rooms:updated': { rooms: LobbyRoom[] };
  'room:changed': { room: LobbyRoom };
  'chat:message': { message: ChatMessage };
  'create_room:requested': void;
  'join_room:requested': { roomId: string };
}

export class LobbyService extends EventEmitter<LobbyServiceEvents> {
  private players: Map<string, LobbyPlayer> = new Map();
  private rooms: Map<string, LobbyRoom> = new Map();
  /**
   * Slim profiles for players who appear in a room's avatar stack but are
   * not in the lobby's "ON THE BLOCK" presence (because they're currently
   * playing). Falls back into getPlayer() so RoomCell mini-avatars resolve.
   */
  private roomPlayers: Map<string, LobbyPlayer> = new Map();
  private messages: ChatMessage[] = [];
  /** Players the local user has blocked — hidden from presence + chat in realtime. */
  private blockedIds: Set<string> = new Set();

  private nm: NetworkManager | null = null;
  private unbinds: Array<() => void> = [];

  // ---------- Lifecycle ----------

  /**
   * Subscribe to the lobby channel and start receiving updates. Idempotent —
   * calling while connected is a no-op. Pair with disconnect() on screen exit.
   */
  connect(networkManager: NetworkManager): void {
    if (this.nm) return;
    this.nm = networkManager;
    this.bindListeners();
    this.nm.subscribeLobby();
  }

  /** Leave the lobby channel and clear all cached state. */
  disconnect(): void {
    if (!this.nm) return;
    this.nm.unsubscribeLobby();
    this.unbinds.forEach((off) => off());
    this.unbinds = [];
    this.players.clear();
    this.rooms.clear();
    this.roomPlayers.clear();
    this.messages = [];
    this.nm = null;
  }

  destroy(): void {
    this.disconnect();
    this.removeAllListeners();
  }

  // ---------- Read API (component-facing — unchanged contract) ----------

  listPlayers(): LobbyPlayer[] {
    return Array.from(this.players.values()).filter((p) => !this.blockedIds.has(p.id));
  }

  /**
   * Update the blocked set and re-render presence in realtime. Called from the
   * BlockService whenever a block/unblock changes. Filtering happens at read
   * time (not by deleting from the cache), so an unblock makes the player
   * reappear instantly without needing the server to re-push presence.
   */
  setBlockedIds(ids: Iterable<string>): void {
    this.blockedIds = new Set(ids);
    this.emit('players:updated', { players: this.listPlayers() });
  }

  /**
   * Resolve a player by id. Falls back to the room-only profile cache so
   * RoomCell can render avatars for players who aren't in the public
   * presence list (because they're inside a room).
   */
  getPlayer(id: string): LobbyPlayer | undefined {
    return this.players.get(id) ?? this.roomPlayers.get(id);
  }

  listRooms(): LobbyRoom[] {
    return Array.from(this.rooms.values());
  }

  listMessages(): ChatMessage[] {
    return [...this.messages];
  }

  // ---------- Action API ----------

  /** Surfaced for the lobby screen's CreateRoomPill — opens the modal. */
  requestCreateRoom(): void {
    this.emit('create_room:requested', undefined);
  }

  /** Tap on a JOIN button — sends to server. Server replies via match:found. */
  requestJoinRoom(roomId: string): void {
    this.nm?.joinLobbyRoom({ roomId });
    this.emit('join_room:requested', { roomId });
  }

  /** Submit from the create-room modal. Server replies via match:found. */
  submitCreateRoom(payload: LobbyCreateRoomPayload): void {
    this.nm?.createLobbyRoom(payload);
  }

  /** Send a public chat message. Server stamps sender from socket auth. */
  sendChat(text: string, _sender?: { id: string; username: string; ringColor: RingColor }): void {
    this.nm?.sendLobbyChat(text);
  }

  // ---------- Network event wiring ----------

  private bindListeners(): void {
    if (!this.nm) return;
    const nm = this.nm;

    // Re-subscribe whenever the underlying socket reconnects. Server-side
    // presence is in-memory and lost across restarts/reconnects; without
    // this, the user would silently appear "still connected" but be invisible
    // to other lobby observers and the snapshot would never refresh.
    const onMainConnected = () => {
      if (this.nm) this.nm.subscribeLobby();
    };

    const onMainDisconnected = () => {
      // Clear local caches so we don't render stale presence/rooms during the
      // reconnect window. The next LOBBY_STATE will hydrate fresh data.
      this.players.clear();
      this.rooms.clear();
      this.roomPlayers.clear();
      this.messages = [];
      this.emit('players:updated', { players: [] });
      this.emit('rooms:updated', { rooms: [] });
    };

    const onState = (data: LobbyStatePayload) => {
      this.players.clear();
      this.rooms.clear();
      this.roomPlayers.clear();
      this.messages = [];

      data.players.forEach((p) => this.players.set(p.id, this.translatePlayer(p)));
      data.rooms.forEach((r) => {
        this.rooms.set(r.id, this.translateRoom(r));
        this.cacheRoomPlayers(r.players);
      });
      data.recentChat.forEach((m) => this.messages.push(this.translateMessage(m)));

      this.emit('players:updated', { players: this.listPlayers() });
      this.emit('rooms:updated', { rooms: this.listRooms() });
    };

    const onPlayerJoined = (e: LobbyPlayerJoinedPayload) => {
      const p = this.translatePlayer(e.player);
      this.players.set(p.id, p);
      this.emit('players:updated', { players: this.listPlayers() });
    };

    const onPlayerLeft = (e: LobbyPlayerLeftPayload) => {
      if (this.players.delete(e.playerId)) {
        this.emit('players:updated', { players: this.listPlayers() });
      }
    };

    const onPlayerUpdated = (e: LobbyPlayerUpdatedPayload) => {
      const p = this.translatePlayer(e.player);
      this.players.set(p.id, p);
      this.emit('players:updated', { players: this.listPlayers() });
    };

    const onChat = (e: LobbyChatMessagePayload) => {
      // Drop chat from players the local user has blocked (system messages have
      // no playerId and always pass through).
      if (e.message.playerId && this.blockedIds.has(e.message.playerId)) return;
      const m = this.translateMessage(e.message);
      this.messages.push(m);
      this.emit('chat:message', { message: m });
    };

    const onRoomUpsert = (e: LobbyRoomUpsertPayload) => {
      const r = this.translateRoom(e.room);
      this.rooms.set(r.id, r);
      this.cacheRoomPlayers(e.room.players);
      this.emit('room:changed', { room: r });
      this.emit('rooms:updated', { rooms: this.listRooms() });
    };

    const onRoomRemoved = (e: LobbyRoomRemovedPayload) => {
      if (this.rooms.delete(e.roomId)) {
        this.emit('rooms:updated', { rooms: this.listRooms() });
      }
    };

    nm.on('main:connected', onMainConnected);
    nm.on('main:disconnected', onMainDisconnected);
    nm.on('lobby:state', onState);
    nm.on('lobby:player_joined', onPlayerJoined);
    nm.on('lobby:player_left', onPlayerLeft);
    nm.on('lobby:player_updated', onPlayerUpdated);
    nm.on('lobby:chat_message', onChat);
    nm.on('lobby:room_upsert', onRoomUpsert);
    nm.on('lobby:room_removed', onRoomRemoved);

    this.unbinds = [
      () => nm.off('main:connected', onMainConnected),
      () => nm.off('main:disconnected', onMainDisconnected),
      () => nm.off('lobby:state', onState),
      () => nm.off('lobby:player_joined', onPlayerJoined),
      () => nm.off('lobby:player_left', onPlayerLeft),
      () => nm.off('lobby:player_updated', onPlayerUpdated),
      () => nm.off('lobby:chat_message', onChat),
      () => nm.off('lobby:room_upsert', onRoomUpsert),
      () => nm.off('lobby:room_removed', onRoomRemoved),
    ];
  }

  // ---------- Translation: wire → display ----------

  private translatePlayer(wire: WireLobbyPlayer): LobbyPlayer {
    let streak: string | undefined;
    let streakCold = false;
    if (wire.todayNet !== undefined && wire.todayNet !== 0) {
      const formatted = formatTodayNet(wire.todayNet);
      streak = formatted.text;
      streakCold = formatted.cold;
    } else if (wire.streak !== undefined && wire.streak > 0) {
      streak = `🔥 W${wire.streak} STREAK`;
    }
    return {
      id: wire.id,
      username: wire.username,
      picture: wire.picture,
      status: wire.status,
      ringColor: ringColorFromId(wire.id),
      // division omitted while leagues are off
      streak,
      streakCold,
    };
  }

  private translateRoom(wire: WireLobbyRoom): LobbyRoom {
    return {
      id: wire.id,
      name: wire.name,
      mode: wire.mode,
      minStake: wire.buyIn,
      maxPlayers: wire.maxPlayers,
      playerIds: wire.players.map((p) => p.id),
      private: wire.isPrivate,
    };
  }

  private translateMessage(wire: WireChatMessage): ChatMessage {
    return {
      id: wire.id,
      kind: wire.kind,
      playerId: wire.playerId,
      username: wire.username,
      picture: wire.picture,
      ringColor: wire.playerId ? ringColorFromId(wire.playerId) : undefined,
      text: wire.text,
      timestamp: wire.timestamp,
    };
  }

  /**
   * Populate roomPlayers cache from a room's denormalized avatar list.
   *
   * Always cache here — do NOT skip players currently in presence. "Presence
   * wins" is enforced at READ time in getPlayer() (it checks `players` first),
   * so caching a room copy never overrides fresher presence. The skip-if-in-
   * presence guard we used to have caused a real bug: when a host creates a
   * room the server fires LOBBY_ROOM_UPSERT (host listed) immediately followed
   * by LOBBY_PLAYER_LEFT (host leaves lobby presence for the game). The upsert
   * ran while the host was still in `players`, so we skipped caching them;
   * player_left then deleted them from `players`, leaving the avatar
   * unresolvable — the room rendered an empty seat for an occupied slot.
   */
  private cacheRoomPlayers(list: WireLobbyRoomPlayer[]): void {
    list.forEach((rp) => {
      this.roomPlayers.set(rp.id, {
        id: rp.id,
        username: rp.username,
        picture: rp.picture,
        ringColor: ringColorFromId(rp.id),
        status: 'busy',
      });
    });
  }
}

// ---------- Pure helpers ----------

const RING_COLORS: RingColor[] = ['gold', 'cyan', 'magenta', 'purple', 'green', 'silver'];

/**
 * Stable ring color from a player id (djb2 hash). Same player always gets
 * the same color across sessions — gives presence a recognisable identity
 * even before we have real cosmetic gear.
 */
function ringColorFromId(id: string): RingColor {
  let hash = 5381;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) + hash + id.charCodeAt(i)) | 0;
  }
  return RING_COLORS[Math.abs(hash) % RING_COLORS.length];
}

function formatTodayNet(net: number): { text: string; cold: boolean } {
  const sign = net >= 0 ? '+' : '-';
  const abs = Math.abs(net);
  let amount: string;
  if (abs >= 1_000_000_000) amount = `${(abs / 1_000_000_000).toFixed(abs % 1_000_000_000 ? 1 : 0)}B`;
  else if (abs >= 1_000_000) amount = `${(abs / 1_000_000).toFixed(abs % 1_000_000 ? 1 : 0)}M`;
  else if (abs >= 1_000) amount = `${Math.round(abs / 1_000)}K`;
  else amount = `${abs}`;
  return { text: `${sign}${amount} TODAY`, cold: net < 0 };
}
